/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — what Track changes compares (SPEC_WORKFLOW §6).

   "This draft versus the published version", for whichever document is on
   screen. Pure, so the Studio can recompute it on every document change
   (it used to be a one-time snapshot, and a variant tab compared the
   UNCHANGED core with itself — "No changes" no matter what you typed).

     · Office Master: published core  vs  the live draft core.
     · A variant:     published core + published layer
                      vs  draft core + the live layer,
                      both composed the way the variant reads (hidden
                      blocks dropped), so a layer edit shows.
   ═══════════════════════════════════════════════════════════════ */

import type { ClientLayer, ContentBlock } from '../../shared/types';
import { compose, toExportBlocks } from '../../shared/spec-compose';
import { buildInlineDiff, type InlineDiffBlock } from '../../shared/block-diff';

export interface TrackChangesInput {
  masterId: string;
  publishedCore: ContentBlock[];
  publishedLayers: ClientLayer[];
  draftCore: ContentBlock[];
  draftLayers: ClientLayer[];
  /** null = the Office Master. */
  layerId: string | null;
}

export function trackChangesSides(input: TrackChangesInput): {
  before: ContentBlock[];
  after: ContentBlock[];
} {
  const { masterId, publishedCore, publishedLayers, draftCore, draftLayers, layerId } = input;
  if (!layerId) return { before: publishedCore, after: draftCore };
  const pubLayer = publishedLayers.find((l) => l.id === layerId);
  const curLayer = draftLayers.find((l) => l.id === layerId);
  return {
    before: toExportBlocks(compose({ id: masterId, coreContent: publishedCore }, pubLayer)),
    after: toExportBlocks(compose({ id: masterId, coreContent: draftCore }, curLayer)),
  };
}

export function trackChangesDiff(input: TrackChangesInput): InlineDiffBlock[] {
  const { before, after } = trackChangesSides(input);
  return buildInlineDiff(before, after);
}

/** Track changes rows → the outline's block list (H2): the outline reads
 *  the headings the diff view shows, removed ones included. */
export function diffOutlineBlocks(blocks: InlineDiffBlock[]): ContentBlock[] {
  return blocks.map((b) => ({
    id: b.id,
    level: b.newLevel ?? b.level ?? 1,
    text: (b.kind === 'removed' ? b.oldText : b.newText || b.oldText) || '',
  }));
}
